/**
 * The single place this app talks to `@tanstack/react-table`.
 *
 * v9 threads a `TFeatures` type parameter through every table type
 * (`ColumnDef<TFeatures, TData, TValue>`, `Row<TFeatures, TData>`, ...). Rather
 * than propagate `typeof features` through ~70 files, this module pins the
 * app's one feature set and re-exports pre-bound aliases. Only `global.d.ts`
 * (for the meta augmentation) should import the package directly.
 *
 * Notably `createColumnHelper` is re-exported with v8's single-generic
 * signature, so call sites keep writing `createColumnHelper<MyRow>()` and only
 * their import path changes.
 */
import {
  columnFilteringFeature,
  columnOrderingFeature,
  columnPinningFeature,
  columnResizingFeature,
  columnSizingFeature,
  columnVisibilityFeature,
  createExpandedRowModel,
  createFilteredRowModel,
  createPaginatedRowModel,
  createSortedRowModel,
  createColumnHelper as createColumnHelperForFeatures,
  rowExpandingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  rowSortingFeature,
  sortFn_alphanumeric,
  sortFn_text,
  tableFeatures,
} from '@tanstack/react-table';
import type {
  Cell,
  CellContext,
  ReactTable,
  TableState,
  Column,
  ColumnDef,
  ColumnMeta,
  Header,
  Row,
  RowData,
  Table,
  TableOptions,
} from '@tanstack/react-table';

/**
 * Every table feature this app uses.
 *
 * v9 only attaches a feature's APIs and state when it is registered here, and a
 * missing feature is NOT a compile error - it surfaces as a method that does
 * not exist or a state slice that is silently ignored. `DataTable.dom.test.tsx`
 * therefore asserts each of these at runtime.
 *
 * Deliberately omitted:
 * - `columnGroupingFeature` / `rowAggregationFeature`: nothing groups *rows*.
 *   `columnHelper.group` only builds header groups, which is core behavior
 *   (`coreHeadersFeature` / `buildHeaderGroups`), not this feature.
 * - `columnFacetingFeature`: filter values are still computed by hand in
 *   `prepareTableData`.
 * - `globalFilteringFeature`, `rowPinningFeature`, `cellSelectionFeature`,
 *   `cellSpanningFeature`: unused.
 *
 * All row models are registered unconditionally even though `DataTable`
 * supports both server- and client-controlled tables. `manualFiltering` /
 * `manualSorting` / `manualPagination` short-circuit to the corresponding
 * `pre*` row model *even when the factory is registered*
 * (`coreRowModelsFeature.utils.js`), so this is behavior-preserving and lets
 * the runtime `clientControlled` flag stay the only switch. Registration is
 * static; the flag is per-render.
 */
const features = tableFeatures({
  // Column sizing is mandatory: every column width in the app comes from
  // `getSize()` / `getStart()`. Resizing additionally requires sizing.
  columnSizingFeature,
  columnResizingFeature,
  columnPinningFeature,
  columnVisibilityFeature,
  columnOrderingFeature,
  columnFilteringFeature,
  rowSortingFeature,
  rowPaginationFeature,
  rowSelectionFeature,
  // Must be unconditional: the custom-child-row tables use row expansion with
  // no `getSubRows` at all, so gating this on `getSubRows` would break them
  // silently at runtime with no type error.
  rowExpandingFeature,
  filteredRowModel: createFilteredRowModel(),
  sortedRowModel: createSortedRowModel(),
  paginatedRowModel: createPaginatedRowModel(),
  expandedRowModel: createExpandedRowModel(),
  // Only the two named sort functions the column definitions actually use.
  // String names resolve solely against what is registered here.
  sortFns: {
    alphanumeric: sortFn_alphanumeric,
    text: sortFn_text,
  },
});

export const appTableFeatures = features;
export type AppFeatures = typeof features;

/**
 * Drop-in replacement for v8's `createColumnHelper<TData>()`.
 *
 * v9's own helper takes `<TFeatures, TData>`; this binds `TFeatures` so the
 * ~44 call sites only change their import path.
 */
export const createColumnHelper = <TData extends RowData>() =>
  createColumnHelperForFeatures<AppFeatures, TData>();

export type AppColumnDef<TData extends RowData, TValue = any> = ColumnDef<
  AppFeatures,
  TData,
  TValue
>;
export type AppTable<TData extends RowData> = Table<AppFeatures, TData>;
/**
 * What `useTable` returns: the core table plus React-only additions
 * (`Subscribe`, `state`, `FlexRender`). `Subscribe` and `atoms` are not on the
 * framework-agnostic `Table` type, so components that subscribe need this.
 */
export type AppReactTable<
  TData extends RowData,
  TSelected = TableState<AppFeatures>,
> = ReactTable<AppFeatures, TData, TSelected>;
export type AppRow<TData extends RowData> = Row<AppFeatures, TData>;
export type AppColumn<TData extends RowData, TValue = any> = Column<
  AppFeatures,
  TData,
  TValue
>;
export type AppCell<TData extends RowData, TValue = any> = Cell<
  AppFeatures,
  TData,
  TValue
>;
export type AppHeader<TData extends RowData, TValue = any> = Header<
  AppFeatures,
  TData,
  TValue
>;
export type AppCellContext<TData extends RowData, TValue = any> = CellContext<
  AppFeatures,
  TData,
  TValue
>;
export type AppColumnMeta<TData extends RowData, TValue = any> = ColumnMeta<
  AppFeatures,
  TData,
  TValue
>;
export type AppTableOptions<TData extends RowData> = TableOptions<
  AppFeatures,
  TData
>;

// Feature-agnostic values and types pass straight through, so that no other
// file needs to import from the package.
export { flexRender, functionalUpdate, useTable } from '@tanstack/react-table';
export type {
  ColumnFiltersState,
  ColumnOrderState,
  ColumnSizingState,
  ExpandedState,
  OnChangeFn,
  PaginationState,
  RowData,
  RowSelectionState,
  SortingState,
  TableState,
  Updater,
  ColumnVisibilityState,
} from '@tanstack/react-table';
